import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import type {BizFormApiClient, BizFormForm, BizFormQuestion, BizFormQuestionResponse} from "../../../../packages/biz-form";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import {QuestionAnswer} from "./question-answer-section";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import {ApiResponseDs} from "../../../../packages/biz-form/core/services/api";

export interface ViewFormResponseDialogProps {
    open: boolean;
    onClose: () => void;
    formId?: string | null;
    responseId?: string | null;
    apiClient: BizFormApiClient;
    getFilePreviewUrl: (fileGuid?: string, fileExtension?: string) => string | undefined;
}

/**
 * Read-only questionnaire viewer (loads {@link BizFormApiClient.getForm} + {@link BizFormApiClient.getFormResponse}).
 */
const ViewFormResponseDialog: React.FC<ViewFormResponseDialogProps> = ({open, onClose, formId, responseId, apiClient, getFilePreviewUrl}) => {
    const isMounted = useIsMounted();
    const [error, setError] = useState<ApiResponseDs>();

    const [loading, setLoading] = useState(false);
    const [pairs, setPairs] = useState<Array<{ question: BizFormQuestion; answer?: BizFormQuestionResponse }>>([]);

    /**
     * Loads form and response data, then pairs questions with their corresponding answers.
     */
    const load = useCallback(async () => {
        if (!formId || !responseId) {
            return;
        }
        setLoading(true);
        const [formRes, responseRes] = await Promise.all([
            apiClient.getForm({id: formId}),
            apiClient.getFormResponse({id: responseId}),
        ]);
        if (!isMounted()) {
            return;
        }
        setLoading(false);
        if (!formRes?.resultFlag || !responseRes?.resultFlag || !formRes.data) {
            setPairs([]);
            setError(formRes);
            return;
        }
        const form = formRes.data as BizFormForm;
        const questions = [...(form.questions ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
        const answers = responseRes.data?.questionResponses ?? [];
        setPairs(
            questions.map((question) => ({
                question,
                answer: answers.find((a) => a.questionId === question.id),
            })),
        );
    }, [apiClient, formId, isMounted, responseId]);

    /**
     * Effect to load form and response data when dialog opens with valid formId and responseId. Resets pairs when dialog closes.
     */
    useEffect(() => {
        if (!open || !formId || !responseId) {
            return;
        }
        void load();
    }, [open, formId, responseId, load]);

    /**
     * Effect to clear question-answer pairs when dialog is closed to prevent showing stale data on next open.
     */
    useEffect(() => {
        if (!open) {
            setPairs([]);
            setError(undefined);
        }
    }, [open]);

    const title = useMemo(() => "Form response", []);

    return (
        <Dialog
            open={open}
            setOpen={(v) => !v && onClose()}
            maxWidth="md"
            className={'bound-dialog'}
        >
            <DialogTitle fontWeight={600} component={'div'}>
                <Typography variant={"h6"} fontWeight={600} sx={{padding: 2}}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, paddingBlockStart: 1, padding: 3, minHeight: '50vh'}}>
                    {
                        loading &&
                        <LoadingLottieAnimation
                            width={'80%'}
                            className={'my-5'}
                        />
                    }
                    {
                        !loading && (error || pairs.length === 0) &&
                        <Card variant={'outlined'} sx={{padding: 2, marginBlock: 'auto'}}>
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, paddingBlock: 4}}>
                                <Typography variant={'body2'} textAlign={'center'}>
                                    We are so sorry but the form response could not be fetched. <br/>
                                    {error?.message ?? ""} <br/>
                                    {
                                        error?.referenceNumber
                                            ? <>` Reference no: ${error.referenceNumber}` <br/> </>
                                            : ""
                                    }
                                    {error?.code ? ` Error Code: ${error.code}` : ""}
                                </Typography>
                                <Button size={'medium'} variant={'outlined'} onClick={() => void load()}>
                                    Retry fetching the form response.
                                </Button>
                            </Box>
                        </Card>
                    }
                    {
                        !loading &&
                        pairs.map(({question, answer}) => (
                            <QuestionAnswer
                                key={question.id}
                                question={question}
                                answer={answer}
                                loading={false}
                                getFilePreviewUrl={getFilePreviewUrl}
                            />
                        ))
                    }
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: 0, display: "flex", justifyContent: "space-between"}}>
                <Button variant="dialog" color="primary" onClick={onClose} borderRadius="0 8px 0 0">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewFormResponseDialog;
export {QuestionAnswer} from "./question-answer-section";